import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isManualResolvableSigIssue, resolveComplianceIssue } from '../utils/complianceIssues';

const ComplianceIssueRow = ({
  issue,
  canResolve,
  resolvingId,
  highlighted,
  onResolve,
}) => (
  <div
    id={`compliance-issue-${issue.id}`}
    className={`alert-item compliance-item${highlighted ? ' compliance-item--highlight' : ''}`}
  >
    <span>
      {issue.description}{' '}
      <span className="badge-alert-gold">Required</span>
    </span>
    {canResolve && isManualResolvableSigIssue(issue) && (
      <button
        type="button"
        className="compliance-resolve-btn"
        disabled={resolvingId === issue.id}
        onClick={() => onResolve(issue)}
      >
        {resolvingId === issue.id ? 'Resolving…' : 'Mark as resolved'}
      </button>
    )}
  </div>
);

export const ComplianceIssueItems = ({
  issues,
  canResolve,
  apiUrl,
  onResolved,
}) => {
  const { user } = useAuth0();
  const [resolvingId, setResolvingId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const scrolledIssueRef = useRef(null);
  const emailPromptedRef = useRef(false);
  const handleResolveRef = useRef(null);

  const handleResolve = useCallback(async (issue) => {
    if (!window.confirm(`Mark this as resolved?\n\n${issue.description}`)) return;
    setResolvingId(issue.id);
    try {
      await resolveComplianceIssue({
        apiUrl,
        issueId: issue.id,
        resolvedBy: user?.email,
      });
      if (typeof onResolved === 'function') onResolved(issue);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to resolve this compliance issue.');
    } finally {
      setResolvingId(null);
    }
  }, [apiUrl, onResolved, user?.email]);
  handleResolveRef.current = handleResolve;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('resolveIssue');
    if (!raw) return;
    const targetId = parseInt(raw, 10);
    if (Number.isNaN(targetId)) return;
    setHighlightId(targetId);

    const target = (issues || []).find((issue) => issue.id === targetId);
    if (!target) return;

    if (scrolledIssueRef.current !== targetId) {
      const el = document.getElementById(`compliance-issue-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrolledIssueRef.current = targetId;
      }
    }

    if (
      canResolve
      && isManualResolvableSigIssue(target)
      && !emailPromptedRef.current
    ) {
      emailPromptedRef.current = true;
      params.delete('resolveIssue');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`,
      );
      handleResolveRef.current?.(target);
    }
  }, [issues, canResolve]);

  return (
    <>
      {issues.map((issue) => (
        <ComplianceIssueRow
          key={issue.id}
          issue={issue}
          canResolve={canResolve}
          resolvingId={resolvingId}
          highlighted={highlightId === issue.id}
          onResolve={handleResolve}
        />
      ))}
    </>
  );
};

const ComplianceIssueBar = ({ issues, canResolve, apiUrl, onResolved }) => {
  if (!issues?.length) return null;
  return (
    <section className="top-alerts-section">
      <div className="alert-banner compliance-banner">
        <div className="alert-title compliance-title">⚠️ Action Required</div>
        <ComplianceIssueItems
          issues={issues}
          canResolve={canResolve}
          apiUrl={apiUrl}
          onResolved={onResolved}
        />
      </div>
    </section>
  );
};

export default ComplianceIssueBar;
